import Analytics from '@/utils/analytics';
import ChainLinkIcon from '@assets/icons/chain-link.svg';
import ChevronRight from '@assets/icons/chevron-right.svg';
import HelpCircleIcon from '@assets/icons/help-circle.svg';
import i18n from '@/utils/i18n';
import MenuCostingIcon from '@assets/icons/menu-costing.svg';
import React, { useCallback, useEffect, useState } from 'react';
import SalesHubSwitchIcon from '@assets/icons/sales-hub-switch.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  APP_CONTAINER_ROUTES,
  CONTAINER_MODAL_ROUTES,
  SALES_PORTAL_TAB_ROUTES,
} from '@/navigators/routes';
import {
  AppContainerStackParams,
  ContainerModalStackParams,
} from '@/navigators/types';
import { Avatar } from '@/components/avatar';
import { Button, Icons } from '@usepepper/sprout-kit-ui';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { Disclosures } from '@/components/disclosures';
import { EMPLOYEE_ROLE_TRANSLATION_MAP } from '@/components/restaurant/restaurant-employee-list';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { Experiments } from '@domain/configuration/experiments';
import { ExternalContentFragment } from '@/types';
import { ExternalContentLinkIcon } from '@/components/external-content/external-content-link-icon';
import { getDisplayedNameText } from '@/utils/string';
import { getMicroProductIconConfig } from '@/screens/storefront-microproducts/micro-product-icon-config';
import {
  isExternalContentStaticLink,
  useExternalContent,
} from '@/contexts/external-content-context';
import { List } from '@/components/list';
import {
  ListItem,
  ListItemSeparator,
  ListOption,
} from '@/components/list-item';
import { MenuItemIcon } from '@/components/menu-item-icon';
import { MicroProduct } from '@domain/microproducts/loaders/use-microproducts-loader';
import { PageContainer } from '@/components/page-container';
import { partnerSupplier } from '@/utils/environment';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import { RedirectActionSheet } from '@/components/redirect-action-sheet';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { SvgProps } from 'react-native-svg';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import {
  useDynamicConfigValue,
  useGetExperiment,
} from '@/hooks/use-dynamic-config-value';
import { useIntercomContext } from '@/contexts/intercom-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSalesPortalNavigationStore } from '@/experiences/sales-portal/blocs/stores/sales-portal-navigation-store';
import { useSignOut } from '@/hooks/use-sign-out';
import { useStorefrontMicroProducts } from '@/screens/storefront-microproducts/blocs/use-storefront-microproducts';

const ICON_SIZE = 32;
const SALES_HUB_ICON_SIZE = 20;
interface AccountListOptionMetadata {
  Icon: React.FC<SvgProps>;
  onPress: () => void;
}

type AccountProfileListOption = ListOption<AccountListOptionMetadata>;
type Navigation = CompositeNavigationProp<
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.Links>,
  StackNavigationProp<ContainerModalStackParams, APP_CONTAINER_ROUTES.Links>
>;

export const AccountProfileScreen: React.FC = () => {
  const intercomContext = useIntercomContext();
  const signOut = useSignOut();

  const [styles, theme] = useStyles();
  const { bottom } = useSafeAreaInsets();
  const { navigate } = useNavigation<Navigation>();
  const {
    selectedRestaurantRelationship: { employeeRole, showMenuCosting },
    supplierEmployeeRole,
    user,
  } = useAuthenticatedUserContext();
  const { switchToExperience } = useExperienceStore();
  const salesPortalConfig = useDynamicConfigValue('sales_portal');
  const { enabled: isSalesPortalNativeMobileEnabled } = useGetExperiment(
    Experiments.SALES_PORTAL_NATIVE_MOBILE,
  );
  const {
    externalContentLinksList,
    fetchExternalContent,
    loadingExternalContent,
    setSelectedExternalContent,
  } = useExternalContent();
  const { loading: loadingMicroProducts, products: storefrontProducts } =
    useStorefrontMicroProducts();

  useEffect(() => {
    fetchExternalContent();
  }, [fetchExternalContent]);
  const [redirectSheetOpen, setRedirectSheetOpen] = useState(false);
  const [selectedExternalLinkURL, setSelectedExternalLinkURL] = useState<
    string | null
  >(null);

  const profileOptions: AccountProfileListOption[] = [];

  if (showMenuCosting) {
    profileOptions.push({
      metadata: {
        Icon: MenuCostingIcon,
        onPress: () => navigate(APP_CONTAINER_ROUTES.MenuInfo),
      },
      title: i18n.t('menu_costing'),
      value: i18n.t('menu_costing'),
    });
  }

  const handleLinkClick = useCallback(
    (link: ExternalContentFragment) => {
      Analytics.track('account_profile_screen.external_content_link.press', {
        externalContentUUID: link.uuid,
      });

      if (isExternalContentStaticLink(link)) {
        setSelectedExternalLinkURL(link.config.url!);
        setRedirectSheetOpen(true);
      } else {
        setSelectedExternalContent(link);
        navigate(APP_CONTAINER_ROUTES.Links);
      }
    },
    [setSelectedExternalContent, navigate],
  );

  const handleMicroProductPress = useCallback(
    (product: MicroProduct) => {
      Analytics.track('account_profile_screen.microproduct.press', {
        microProductId: product.id,
      });

      navigate(APP_CONTAINER_ROUTES.StorefrontMicroProduct, {
        productId: product.id,
      });
    },
    [navigate],
  );

  // Microproducts before links, matching the drawer's More menu.
  storefrontProducts.forEach((product) => {
    profileOptions.push({
      metadata: {
        Icon: () => (
          <MenuItemIcon
            item={getMicroProductIconConfig(
              product,
              // Chain-link paths are fill-based, so a fill tint is safe here
              // (unlike the stroke-based external content link icons).
              <ChainLinkIcon
                fill={theme.colors.primary}
                height={ICON_SIZE}
                width={ICON_SIZE}
              />,
            )}
            size={ICON_SIZE}
          />
        ),
        onPress: () => handleMicroProductPress(product),
      },
      title: product.displayName,
      value: product.id,
    });
  });

  if (externalContentLinksList.length > 0) {
    externalContentLinksList.forEach(({ metadata: externalContent }) => {
      if (externalContent) {
        profileOptions.push({
          metadata: {
            Icon: () => (
              <ExternalContentLinkIcon
                iconSize={ICON_SIZE}
                link={externalContent}
              />
            ),
            onPress: () => handleLinkClick(externalContent),
          },
          title: getDisplayedNameText(
            externalContent.display_name,
            externalContent.name,
          ),
          value: externalContent.uuid,
        });
      }
    });
  }

  if (intercomContext.initialized) {
    profileOptions.push({
      metadata: {
        Icon: () => <HelpCircleIcon stroke={theme.colors.primary} />,
        onPress: () => {
          Analytics.track('account_profile_screen.support_button');
          intercomContext.toggle();
        },
      },
      title: i18n.t('support'),
      value: i18n.t('support'),
    });
  }

  const isSalesPortalSwitchEnabled =
    Boolean(supplierEmployeeRole) &&
    !!salesPortalConfig?.enabled &&
    (Platform.OS === 'web' || isSalesPortalNativeMobileEnabled);

  const logOutOption: AccountProfileListOption = {
    metadata: {
      Icon: () => (
        <Icons.LogOut color={theme.colors.primary} size={ICON_SIZE} />
      ),
      onPress: () => {
        Analytics.track('account_screen.logout_button.press');
        signOut();
      },
    },
    title: i18n.t('log_out'),
    value: i18n.t('log_out'),
  };

  const onSelectProfileOption = ({ metadata }: AccountProfileListOption) =>
    metadata!.onPress();

  const renderProfileOptionIcon = ({ metadata }: AccountProfileListOption) => {
    const Icon = metadata!.Icon;
    return <Icon height={ICON_SIZE} width={ICON_SIZE} />;
  };

  const onAvatarPress = partnerSupplier.demoEnabled
    ? () => navigate(CONTAINER_MODAL_ROUTES.DemoSettings)
    : undefined;

  return (
    <PageContainer
      navigationHeaderProps={{
        showBackButton: true,
        showShadow: false,
      }}
    >
      <View
        style={[
          styles.containerWrapper,
          { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
        ]}
      >
        <View style={styles.headerContainer}>
          <TouchableOpacity
            disabled={!onAvatarPress}
            onPress={onAvatarPress}
            style={styles.avatar}
          >
            <Avatar name={user.name} size="small" uuid={user.uuid} />
          </TouchableOpacity>
          <Text style={TYPOGRAPHY.h2}>{user.name}</Text>
          <Text style={[TYPOGRAPHY.p, styles.employeeRole]}>
            {EMPLOYEE_ROLE_TRANSLATION_MAP[employeeRole!]}
          </Text>
          <TouchableOpacity
            onPress={() => {
              Analytics.track(
                'account_profile_screen.view_profile_button.press',
              );
              navigate(APP_CONTAINER_ROUTES.Account, {
                hideActions: true,
              });
            }}
            style={styles.profile}
          >
            <Text style={[TYPOGRAPHY.pBold, styles.profileText]}>
              {i18n.t('view_profile')}
            </Text>
          </TouchableOpacity>
        </View>
        {loadingExternalContent || loadingMicroProducts ? (
          <View style={styles.externalContentLoader}>
            <ActivityIndicator />
          </View>
        ) : (
          <List<AccountProfileListOption>
            data={profileOptions}
            ListFooterComponent={
              <>
                <ListItemSeparator />
                <ListItem<AccountProfileListOption>
                  item={logOutOption}
                  listItemStyle={styles.listItem}
                  onSelect={onSelectProfileOption}
                  renderLeftComponent={renderProfileOptionIcon}
                />
              </>
            }
            listItemStyle={styles.listItem}
            onSelect={onSelectProfileOption}
            renderLeftComponent={renderProfileOptionIcon}
            renderRightComponent={() => (
              <ChevronRight fill={theme.colors.inactiveFill} />
            )}
          />
        )}
        <View style={styles.footerContainer}>
          {isSalesPortalSwitchEnabled ? (
            <View style={styles.footerButton}>
              <Button
                fullWidth
                icon={
                  <SalesHubSwitchIcon
                    height={SALES_HUB_ICON_SIZE}
                    stroke={theme.sk.color.icon.primary}
                    width={SALES_HUB_ICON_SIZE}
                  />
                }
                onPress={() => {
                  Analytics.track(
                    'account_screen.switch_to_sales_hub_button.press',
                  );
                  useSalesPortalNavigationStore
                    .getState()
                    .setLastActiveRoute(SALES_PORTAL_TAB_ROUTES.OrderEntry);
                  switchToExperience(ExperienceMode.SALES_PORTAL);
                }}
                size="$lg"
                variant="secondary"
              >
                {i18n.t('switch_to_sales_hub')}
              </Button>
            </View>
          ) : null}
          <View style={styles.footer}>
            <Disclosures />
          </View>
        </View>
        <RedirectActionSheet
          isRedirectSheetOpen={redirectSheetOpen}
          redirectURL={selectedExternalLinkURL}
          setIsRedirectSheetOpen={setRedirectSheetOpen}
        />
      </View>
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  avatar: {
    marginBottom: 16,
  },
  containerWrapper: {
    flex: 1,
  },
  employeeRole: {
    color: theme.colors.inactiveText,
  },
  externalContentLoader: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  footer: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    marginTop: 16,
    paddingTop: 16,
  },
  footerButton: {
    marginHorizontal: SCREEN_PADDING,
  },
  footerContainer: {
    justifyContent: 'flex-end',
    paddingTop: 16,
  },
  headerContainer: {
    alignItems: 'center',
    borderBottomColor: theme.colors.offWhite,
    borderBottomWidth: 4,
    paddingBottom: 8,
  },
  listItem: {
    paddingVertical: 24,
  },
  profile: {
    marginTop: 16,
  },
  profileText: {
    color: theme.colors.darkGreen,
  },
}));
